<template>
  <div class="service-detail-page">
    <v-container class="py-4">
      <!-- Back Header -->
      <div class="page-header mb-3">
        <div class="page-header-row">
          <v-btn icon @click="$router.back()" class="back-btn">
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
          <div class="page-header-text">
            <div class="page-header-label">Service Details</div>
            <div class="page-header-title">{{ service?.title || "Service" }}</div>
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <GlobalLoader v-if="loading" message="Loading service details..." />

      <!-- Location Required State -->
      <v-row v-else-if="locationRequired" justify="center" class="py-16">
        <v-col cols="12" md="8" class="text-center">
          <v-icon size="72" color="#f46523">mdi-map-marker-alert-outline</v-icon>
          <h3 class="mt-4">Location permission required</h3>
          <p class="grey--text mt-2">
            {{ locationMessage }}
          </p>
          <v-btn color="#f46523" dark class="mt-4" @click="handleEnableLocationClick">
            Enable Location
          </v-btn>
          <div class="location-how-to-box">
            <h4 class="location-how-to-title">
              <v-icon small class="mr-1">mdi-information-outline</v-icon>
              How to enable location (if the browser prompt doesn’t appear)
            </h4>
            <div class="location-how-to-steps">
              <p class="location-how-to-browser mb-1"><strong>Chrome / Edge</strong></p>
              <ol>
                <li>Click the <strong>lock</strong> or <strong>info</strong> icon left of the address bar → <strong>Site settings</strong> → <strong>Location</strong> → <strong>Allow</strong>.</li>
                <li>Refresh the page or click Enable Location again.</li>
              </ol>
              <p class="location-how-to-browser mb-1 mt-3"><strong>Safari</strong></p>
              <ol>
                <li>Settings → Safari → Location (or Settings for This Website → Location) → <strong>Allow</strong> or <strong>Ask</strong>, then reload.</li>
              </ol>
            </div>
            <div class="location-guide-links mt-3">
              <span class="location-guide-links-label">Open browser guide:</span>
              <a href="https://support.google.com/chrome/answer/114662" target="_blank" rel="noopener noreferrer" class="location-guide-link">Chrome / Edge</a>
              <span class="location-guide-sep">·</span>
              <a href="https://support.apple.com/guide/safari/websites-ibrwe2159985/mac" target="_blank" rel="noopener noreferrer" class="location-guide-link">Safari</a>
            </div>
            <p class="location-tip mt-3 mb-0">
              If the prompt still doesn’t appear (e.g. you blocked it before), allow location in browser settings above, or open this page in a <strong>private / incognito window</strong> and the prompt will show again.
            </p>
          </div>
        </v-col>
      </v-row>

      <!-- Service Content -->
      <v-row v-else-if="service" class="service-content">
        <!-- Left Column - Image -->
        <v-col cols="12" md="5" lg="5">
          <v-card elevation="2" class="image-card">
            <v-img
              :src="service.image?.[0] || placeholderImage"
              height="350"
              cover
              class="rounded-lg"
            >
              <template v-slot:placeholder>
                <v-row class="fill-height ma-0" align="center" justify="center">
                  <v-progress-circular indeterminate color="#f46523" />
                </v-row>
              </template>
            </v-img>
          </v-card>
        </v-col>

        <!-- Right Column - Details -->
        <v-col cols="12" md="7" lg="7">
          <div class="service-details-card">
            <!-- Title & Price Section -->
            <div class="title-section mb-3">
              <h1 class="service-title">{{ service.title }}</h1>
              <div class="price-tag mt-2">
                <span class="price-label">Starting from</span>
                <h2 class="price-amount" :class="{ free: isFree }">
                  <span v-if="isFree"
                    >₹ <span class="price-free">Free</span></span
                  >
                  <span v-else>₹{{ selectedServiceType?.price || "-" }}</span>
                </h2>
              </div>
            </div>

            <!-- Service Type Selection -->
            <div
              v-if="serviceTypes.length"
              class="service-selection-section mb-3"
            >
              <h3 class="section-heading">
                <v-icon color="#f46523" size="18" class="mr-1"
                  >mdi-format-list-checks</v-icon
                >
                Select Service Type
              </h3>
              <v-card class="service-types-card" outlined>
                <div
                  v-for="type in serviceTypes"
                  :key="type._id"
                  class="service-type-item"
                  :class="{ active: selectedServiceType?._id === type._id }"
                  @click="handleSelectServiceType(type)"
                >

                  <div class="radio-wrapper">
                    <v-icon
                      :color="
                        selectedServiceType?._id === type._id
                          ? '#f46523'
                          : '#bbb'
                      "
                      size="20"
                    >
                      {{
                        selectedServiceType?._id === type._id
                          ? "mdi-radiobox-marked"
                          : "mdi-radiobox-blank"
                      }}
                    </v-icon>
                  </div>
                  <div class="service-type-details">
                    <span class="service-type-name">{{ type.name }}</span>
                    <span class="service-type-price">₹{{ type.price }}</span>
                  </div>
                </div>
              </v-card>
            </div>

            <!-- Scrollable Content Area -->
            <div class="scrollable-content">
              <!-- Description Section -->
              <div class="description-section mb-3">
                <h3 class="section-heading">
                  <v-icon color="#f46523" size="18" class="mr-1"
                    >mdi-information</v-icon
                  >
                  Description
                </h3>
                <p class="service-description">
                  {{ service.description?.text || "No description available." }}
                </p>
              </div>

              <!-- Included Services -->
              <div
                v-if="service.description?.included?.length"
                class="included-section mb-3"
              >
                <h3 class="section-heading">
                  <v-icon color="#4caf50" size="18" class="mr-1"
                    >mdi-check-circle</v-icon
                  >
                  What's Included
                </h3>
                <v-card class="info-card" outlined>
                  <ul class="included-list">
                    <li
                      v-for="(item, i) in service.description.included"
                      :key="i"
                      class="included-item"
                    >
                      <v-icon color="#4caf50" size="16" class="mr-2"
                        >mdi-check</v-icon
                      >
                      <span>{{ item }}</span>
                    </li>
                  </ul>
                </v-card>
              </div>

              <!-- Excluded Services -->
              <div
                v-if="service.description?.excluded?.length"
                class="excluded-section mb-2"
              >
                <h3 class="section-heading">
                  <v-icon color="#f44336" size="18" class="mr-1"
                    >mdi-close-circle</v-icon
                  >
                  Not Included
                </h3>
                <v-card class="info-card" outlined>
                  <ul class="excluded-list">
                    <li
                      v-for="(item, i) in service.description.excluded"
                      :key="i"
                      class="excluded-item"
                    >
                      <v-icon color="#f44336" size="16" class="mr-2"
                        >mdi-close</v-icon
                      >
                      <span>{{ item }}</span>
                    </li>
                  </ul>
                </v-card>
              </div>
            </div>

            <!-- CTA Button - Moved Down -->
            <div class="cta-section mt-4">
              <v-btn
                color="#f46523"
                dark
                large
                depressed
                block
                class="book-now-btn"
                :disabled="!selectedServiceType"
                @click="openVerifyDialog"
              >
                <v-icon left>mdi-calendar-check</v-icon>
                Book Now
              </v-btn>
            </div>

            <p class="help-text mt-2">
              <v-icon small color="#666" class="mr-1"
                >mdi-information-outline</v-icon
              >
              Final price may vary based on requirements
            </p>
          </div>
        </v-col>
      </v-row>

      <!-- Empty State -->
      <v-row v-else justify="center" class="py-16">
        <v-col cols="12" class="text-center">
          <v-icon size="80" color="#ccc"
            >mdi-file-document-remove-outline</v-icon
          >
          <h3 class="mt-4 grey--text text--darken-1">Service not found</h3>
          <p class="grey--text mt-2">
            The service you're looking for doesn't exist or has been removed.
          </p>
          <v-btn
            color="#f46523"
            text
            class="mt-4"
            @click="goBackToServices"
          >
            <v-icon left>mdi-arrow-left</v-icon>
            Back to Services
          </v-btn>
        </v-col>
      </v-row>
    </v-container>

    <!-- Verify Dialog (OTP) -->
    <v-dialog 
      v-model="verifyDialog" 
      max-width="420" 
      persistent
      overlay-opacity="0.7"
      style="z-index: 9999 !important;"
    >
      <v-card class="otp-dialog" elevation="24">
        <!-- Header with gradient background -->
        <div class="dialog-header">
          <div class="header-content">
            <h2 class="dialog-title-text">Verify Your Number</h2>
            <p class="dialog-subtitle">We'll send you a one-time password</p>
          </div>
          <v-btn 
            icon 
            absolute 
            top 
            right 
            class="close-btn"
            @click="verifyDialog = false"
          >
            <v-icon color="white">mdi-close</v-icon>
          </v-btn>
        </div>

        <v-card-text class="dialog-body">
          <!-- Mobile Input -->
          <div v-if="!otpSent" class="mobile-section">
            <div class="input-label">
              <v-icon small color="#f46523" class="mr-1">mdi-phone</v-icon>
              <span>Mobile Number</span>
            </div>
            <v-text-field
              v-model="mobile"
              placeholder="Enter 10-digit mobile number"
              outlined
              type="tel"
              maxlength="10"
              class="mobile-input"
              :rules="[
                (v) => !!v || 'Mobile number is required',
                (v) => v.length === 10 || 'Must be 10 digits',
              ]"
              prepend-inner-icon="mdi-cellphone"
              hide-details="auto"
            />
            
            <v-btn
              color="#f46523"
              :dark="mobile.length === 10"
              :outlined="mobile.length !== 10"
              block
              x-large
              depressed
              class="send-otp-btn mt-6"
              :loading="otpLoading"
              :disabled="mobile.length !== 10"
              @click="sendOtp"
            >
              <v-icon left>mdi-send</v-icon>
              Send OTP
            </v-btn>

            <div class="info-box mt-4">
              <v-icon small color="#4caf50" class="mr-2">mdi-information</v-icon>
              <span>You'll receive a 4-digit OTP on this number</span>
            </div>
          </div>

          <!-- OTP Input -->
          <div v-else class="otp-section">
            <div class="otp-header-info">
              <p class="otp-sent-msg">
                We've sent a code to
              </p>
              <p class="mobile-display">
                <strong>+91 {{ mobile }}</strong>
                <v-btn 
                  text 
                  x-small 
                  color="#f46523" 
                  class="ml-2"
                  @click="otpSent = false"
                >
                  <v-icon small left>mdi-pencil</v-icon>
                  Change
                </v-btn>
              </p>
            </div>

            <div class="otp-label mt-6 mb-3">Enter OTP</div>
            <div class="otp-inputs mb-5">
              <v-text-field
                v-for="(digit, index) in otpArray"
                :key="index"
                v-model="otpArray[index]"
                outlined
                type="tel"
                maxlength="1"
                class="otp-input"
                hide-details
                @input="handleOtpInput(index)"
              />
            </div>

            <div v-if="otpErrorMessage" class="otp-error-message mb-4">
              {{ otpErrorMessage }}
            </div>

            <v-btn
              color="#f46523"
              :dark="otpArray.join('').length === 4"
              :outlined="otpArray.join('').length !== 4"
              block
              x-large
              depressed
              class="verify-btn"
              :loading="verifyLoading"
              :disabled="otpArray.join('').length !== 4"
              @click="verifyOtp"
            >
              <v-icon left>mdi-check-circle</v-icon>
              Verify & Continue
            </v-btn>

            <div class="resend-section mt-4">
              <span class="resend-text">Didn't receive the code?</span>
              <v-btn
                text
                small
                color="#f46523"
                class="resend-btn"
                @click="sendOtp"
              >
                <v-icon small left>mdi-refresh</v-icon>
                Resend OTP
              </v-btn>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

  </div>
</template>

<script>
import { sendOtpApi, verifyOtpApi } from "../services/auth";
import {
  fetchServiceByGuestUser,
  selectServiceTypeApi,
} from "../services/services.service";
import GlobalLoader from "@/components/GlobalLoader.vue";
import { slugify, slugToMatch } from "@/utils/slug";
import { enrichAcServiceContent } from "@/utils/acServiceContent";

export default {
  name: "CategoryDetailedView",
  components: {
    GlobalLoader,
  },

  data() {
    return {
      service: null,
      serviceTypes: [],
      selectedServiceType: null,
      loading: true,
      placeholderImage:
        "https://via.placeholder.com/600x400?text=Service+Image",
      resolvedServiceId: null,

      // OTP Dialog
      verifyDialog: false,
      mobile: "",
      otpArray: ["", "", "", ""],
      otpErrorMessage: "",
      otpSent: false,
      otpLoading: false,
      verifyLoading: false,
      locationRequired: false,
      locationMessage:
        "Please allow location access to view service availability in your area.",
    };
  },

  computed: {
    serviceSlug() {
      const slug = this.$route.params.serviceSlug;
      return slug ? decodeURIComponent(slug).toLowerCase() : "";
    },
    isFree() {
      return Number(this.selectedServiceType?.price) === 0;
    },
  },

  watch: {
    serviceSlug() {
      this.resolvedServiceId = null;
      this.initWithLocation();
    },
  },

  mounted() {
    this.initWithLocation();
  },

  methods: {
    getKochiFallbackLocation() {
      const location = {
        latitude: 9.9312,
        longitude: 76.2673,
        allowed: true,
        error: null,
        isFallback: true,
      };
      try {
        localStorage.setItem("userLocation", JSON.stringify(location));
        sessionStorage.setItem("userLocation", JSON.stringify(location));
      } catch (e) {
        /* ignore */
      }
      return location;
    },
    getLocationFromStorage() {
      try {
        const raw =
          localStorage.getItem("userLocation") ||
          sessionStorage.getItem("userLocation");
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (parsed?.latitude == null || parsed?.longitude == null) return null;
        return parsed;
      } catch (e) {
        return null;
      }
    },
    async initWithLocation() {
      if (
        typeof window !== "undefined" &&
        !window.isSecureContext &&
        window.location.hostname !== "localhost"
      ) {
        this.locationRequired = true;
        this.locationMessage =
          "Location requires a secure connection (HTTPS). Please open this site on HTTPS and retry.";
        this.loading = false;
        return;
      }
      if (!navigator.geolocation) {
        this.locationRequired = true;
        this.locationMessage = "Geolocation is not supported in this browser.";
        this.loading = false;
        return;
      }
      const stored = this.getLocationFromStorage();
      if (stored) {
        this.resolveServiceId();
        await this.callGuestServiceApi(stored);
        return;
      }
      // Request location on load so the browser prompt can appear when landing directly on this page (same as home).
      this.requestLocationAndLoad();
    },
    resolveServiceId() {
      const slug = this.serviceSlug;
      if (!slug) return;
      if (/^[a-f0-9]{24}$/i.test(slug)) {
        this.resolvedServiceId = slug;
        return;
      }
      const baseSlug = slugToMatch(slug) || slug;
      const fromStorage = sessionStorage.getItem(`service_slug_${slug}`) || sessionStorage.getItem(`service_slug_${baseSlug}`);
      if (fromStorage) {
        this.resolvedServiceId = fromStorage;
        return;
      }
      const selected = sessionStorage.getItem("selectedService");
      if (selected) {
        try {
          const parsed = JSON.parse(selected);
          if ((slugify(parsed?.title || "") === slug || slugify(parsed?.title || "") === baseSlug) && parsed?._id) {
            this.resolvedServiceId = parsed._id;
            sessionStorage.setItem(`service_slug_${slug}`, parsed._id);
            return;
          }
        } catch (e) {
          /* ignore */
        }
      }
      this.resolvedServiceId = null;
    },

    goBackToServices() {
      const isUSRoute =
        this.$route.meta?.location === "us" ||
        this.$route.path.startsWith("/us");
      this.$router.push({ name: isUSRoute ? "ServicesUS" : "Services" });
    },

    async     handleEnableLocationClick() {
      this.requestLocationAndLoad();
    },

    async isLocationPermissionBlocked() {
      try {
        if (!navigator.permissions?.query) return false;
        const permission = await navigator.permissions.query({
          name: "geolocation",
        });
        return permission.state === "denied";
      } catch (error) {
        return false;
      }
    },

    tryRevokeLocationPermission() {
      try {
        if (!navigator.permissions?.query) return;
        navigator.permissions.query({ name: "geolocation" }).then((p) => {
          if (typeof p.revoke === "function") p.revoke();
        });
      } catch (e) {
        /* ignore */
      }
    },

    requestLocationAndLoad() {
      this.tryRevokeLocationPermission();
      this.locationMessage = "Requesting location permission...";
      this.loading = true;
      if (
        typeof window !== "undefined" &&
        !window.isSecureContext &&
        window.location.hostname !== "localhost"
      ) {
        this.locationRequired = true;
        this.locationMessage =
          "Location requires a secure connection (HTTPS). Please open this site on HTTPS and retry.";
        this.loading = false;
        return;
      }

      if (!navigator.geolocation) {
        const fallback = this.getKochiFallbackLocation();
        this.locationRequired = false;
        this.locationMessage = "Using Kochi location to continue.";
        this.resolveServiceId();
        if (!this.resolvedServiceId) {
          this.service = null;
          this.loading = false;
          return;
        }
        this.callGuestServiceApi(fallback);
        return;
      }

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const location = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            allowed: true,
            error: null,
          };
          localStorage.setItem("userLocation", JSON.stringify(location));
          sessionStorage.setItem("userLocation", JSON.stringify(location));
          this.locationRequired = false;
          this.resolveServiceId();
          if (!this.resolvedServiceId) {
            this.service = null;
            this.loading = false;
            return;
          }
          await this.callGuestServiceApi(location);
        },
        () => {
          const fallback = this.getKochiFallbackLocation();
          this.locationRequired = false;
          this.locationMessage = "Using Kochi location to continue.";
          this.resolveServiceId();
          if (!this.resolvedServiceId) {
            this.service = null;
            this.loading = false;
            return;
          }
          this.callGuestServiceApi(fallback);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        }
      );
    },

    async handleSelectServiceType(type) {
      if (!type?._id) return;

      try {
        // set locally for UI highlight
        this.selectedServiceType = type;

        // call API with token automatically
        await selectServiceTypeApi(type._id);

        console.log("✅ Service type selected:", type._id);
      } catch (err) {
        console.error("❌ Service type selection failed:", err);
      }
    },

    async callGuestServiceApi(locationOverride) {
      try {
        let latitude;
        let longitude;
        if (locationOverride) {
          latitude = locationOverride.latitude;
          longitude = locationOverride.longitude;
        } else {
          const locationRaw =
            localStorage.getItem("userLocation") ||
            sessionStorage.getItem("userLocation");
          if (!locationRaw) {
            this.locationRequired = true;
            this.locationMessage =
              "Please allow location permission to continue with service booking.";
            this.loading = false;
            return;
          }
          const parsedLocation = JSON.parse(locationRaw);
          latitude = parsedLocation.latitude;
          longitude = parsedLocation.longitude;
        }

        if (latitude == null || longitude == null) {
          this.locationRequired = true;
          this.locationMessage =
            "Location is required to fetch nearby service availability.";
          this.loading = false;
          return;
        }

        // Keep latest location in localStorage for downstream booking flow.
        localStorage.setItem(
          "userLocation",
          JSON.stringify({ latitude, longitude })
        );

        const res = await fetchServiceByGuestUser({
          serviceId: this.resolvedServiceId,
          latitude,
          longitude,
        });

        const { service, serviceTypes } = res.data.data;

        this.service = enrichAcServiceContent(service);
        this.serviceTypes = serviceTypes || [];
        this.selectedServiceType = this.serviceTypes[0] || null;
      } catch (err) {
        console.error("❌ Guest service API error:", err);
        this.locationRequired = false;
      } finally {
        this.loading = false;
      }
    },

    openVerifyDialog() {
      const token = localStorage.getItem("access_token");

      const payload = {
        serviceTypeId: this.selectedServiceType?._id,
        serviceName: this.service?.title,
      };
      sessionStorage.setItem("bookingPayload", JSON.stringify(payload));
      if (token) {
        this.$router.push({
          name: "AddBookings",
          params: payload,
        });
        return;
      }

      this.verifyDialog = true;
    },

    async sendOtp() {
      if (this.mobile.length !== 10) return;
      this.otpLoading = true;
      this.otpErrorMessage = "";
      try {
        const mobileNumber = `+91${this.mobile}`;
        await sendOtpApi(mobileNumber);
        this.otpSent = true;
      } catch (err) {
        console.error("OTP send error:", err);
      } finally {
        this.otpLoading = false;
      }
    },

    async verifyOtp() {
      const otp = this.otpArray.join("");
      if (otp.length !== 4) return;

      this.verifyLoading = true;
      this.otpErrorMessage = "";
      try {
        const mobileNumber = `+91${this.mobile}`;
        const res = await verifyOtpApi({
          mobileNumber,
          otp,
          device_type: "web",
          device_token: "web",
        });

        const accessToken = res?.data?.data?.access_token;
        const isOtpVerified =
          res?.data?.error_code === 0 &&
          typeof accessToken === "string" &&
          accessToken.trim() !== "" &&
          accessToken !== "undefined" &&
          accessToken !== "null";

        if (isOtpVerified) {
          localStorage.setItem("access_token", accessToken);
          localStorage.setItem("mobile_number", this.mobile);
          this.$bus.$emit("auth-change");
          this.verifyDialog = false;
          const payload = JSON.parse(
            sessionStorage.getItem("bookingPayload") || "null"
          );

          this.$router.push({
            name: "AddBookings",
            params: payload || {},
          });
          return;
        }

        this.otpErrorMessage =
          res?.data?.message || "Invalid OTP. Please enter the correct code.";
        this.resetOtpInputs();
      } catch (err) {
        console.error("OTP verification error:", err);
        this.otpErrorMessage =
          err?.response?.data?.message ||
          "Invalid OTP. Please enter the correct code.";
        this.resetOtpInputs();
      } finally {
        this.verifyLoading = false;
      }
    },

    resetOtpInputs() {
      this.otpArray = ["", "", "", ""];
      this.$nextTick(() => {
        const firstInput = document.querySelectorAll(".otp-input input")[0];
        if (firstInput) firstInput.focus();
      });
    },

    handleOtpInput(index) {
      this.otpArray[index] = String(this.otpArray[index] || "")
        .replace(/\D/g, "")
        .slice(0, 1);
      this.otpErrorMessage = "";

      // Auto-focus next input
      if (this.otpArray[index] && index < 3) {
        const nextInput =
          document.querySelectorAll(".otp-input input")[index + 1];
        if (nextInput) nextInput.focus();
      }

      const isComplete = this.otpArray.every((digit) => digit.length === 1);
      if (index === 3 && isComplete && !this.verifyLoading) {
        this.verifyOtp();
      }
    },
  },
};
</script>

<style scoped>
/* Page Wrapper */
.service-detail-page {
  background: #f8f9fa;
  min-height: 100vh;
}

/* Header */
.page-header {
  background: white;
  padding: 16px 18px;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}
.page-header-row {
  display: flex;
  align-items: center;
  gap: 12px;
}
.back-btn {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: #fff3ed;
}
.page-header-text {
  min-width: 0;
}
.page-header-label {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: #f46523;
}
.page-header-title {
  font-size: 18px;
  font-weight: 700;
  color: #1a1a1a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Image Card */
.image-card {
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

/* Service Details Card */
.service-details-card {
  background: white;
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  display: flex;
  flex-direction: column;
  height: 100%;
}

/* Title Section */
.title-section {
  border-bottom: 2px solid #f5f5f5;
  padding-bottom: 12px;
}

.service-title {
  font-size: 26px;
  font-weight: 700;
  color: #1a1a1a;
  line-height: 1.3;
  margin: 0;
}

.price-tag {
  display: flex;
  align-items: baseline;
  gap: 10px;
}

.price-label {
  font-size: 13px;
  color: #666;
  font-weight: 500;
}

.price-amount {
  font-size: 28px;
  font-weight: 700;
  color: #f46523;
  margin: 0;
}
.price-amount.free {
  color: #1a1a1a;
}
.price-free {
  color: #66bb6a;
  font-weight: 800;
}

/* Section Headings */
.section-heading {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
}

/* Service Type Selection */
.service-types-card {
  border-radius: 8px;
  overflow: hidden;
}

.service-type-item {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  cursor: pointer;
  transition: all 0.2s;
  border-bottom: 1px solid #f0f0f0;
}

.service-type-item:last-child {
  border-bottom: none;
}

.service-type-item:hover {
  background: #fff5f0;
}

.service-type-item.active {
  background: #fff5f0;
  border-left: 4px solid #f46523;
}

.radio-wrapper {
  margin-right: 12px;
}

.service-type-details {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex: 1;
}

.service-type-name {
  font-size: 14px;
  font-weight: 500;
  color: #333;
}

.service-type-price {
  font-size: 15px;
  font-weight: 600;
  color: #f46523;
}

/* CTA Section - Fixed Position */
.cta-section {
  border-top: 2px solid #f5f5f5;
  padding: 16px 0 4px;
}

.book-now-btn {
  font-size: 15px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  border-radius: 8px;
  height: 48px !important;
}

/* Scrollable Content Area */
.scrollable-content {
  max-height: 280px;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 8px;
  margin-right: -8px;
}

/* Custom Scrollbar */
.scrollable-content::-webkit-scrollbar {
  width: 6px;
}

.scrollable-content::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

.scrollable-content::-webkit-scrollbar-thumb {
  background: #f46523;
  border-radius: 10px;
}

.scrollable-content::-webkit-scrollbar-thumb:hover {
  background: #d94e15;
}

/* Description */
.service-description {
  font-size: 16px;
  line-height: 1.6;
  color: #555;
  margin: 0;
}

/* Info Cards (Included/Excluded) */
.info-card {
  border-radius: 8px;
  padding: 4px;
}

.included-list,
.excluded-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.included-item,
.excluded-item {
  display: flex;
  align-items: flex-start;
  padding: 10px 12px;
  font-size: 15px;
  line-height: 1.5;
}

.included-item {
  color: #333;
}

.excluded-item {
  color: #666;
}

.help-text {
  font-size: 12px;
  color: #666;
  text-align: center;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* OTP Dialog */
.otp-dialog {
  border-radius: 16px !important;
  overflow: hidden;
  z-index: 10000 !important;
}

/* Ensure dialog overlay is above header */
::v-deep .v-dialog__content {
  z-index: 9999 !important;
}

::v-deep .v-overlay {
  z-index: 9998 !important;
}

/* Dialog Header with Gradient */
.dialog-header {
  background: linear-gradient(135deg, #f46523 0%, #ff8c42 100%);
  padding: 24px 20px;
  position: relative;
  text-align: center;
}

.header-content {
  position: relative;
  z-index: 1;
}

.dialog-title-text {
  font-size: 22px;
  font-weight: 700;
  color: white;
  margin: 0 0 6px 0;
  letter-spacing: -0.5px;
}

.dialog-subtitle {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.9);
  margin: 0;
  font-weight: 400;
}

.close-btn {
  margin: 8px;
  z-index: 10 !important;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.1) !important;
}

/* Dialog Body */
.dialog-body {
  padding: 24px 22px !important;
  background: #fafafa;
}

/* Mobile Section */
.mobile-section {
  animation: fadeInUp 0.4s ease;
}

.input-label {
  display: flex;
  align-items: center;
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 10px;
}

.mobile-input {
  margin-top: 0 !important;
}

.mobile-input >>> .v-input__control {
  background: white;
  border-radius: 12px;
}

.mobile-input >>> input {
  font-size: 16px;
  font-weight: 500;
  letter-spacing: 0.5px;
}

.send-otp-btn {
  border-radius: 12px !important;
  font-weight: 600 !important;
  letter-spacing: 0.5px;
  text-transform: none !important;
  font-size: 16px !important;
  box-shadow: 0 4px 12px rgba(244, 101, 35, 0.3) !important;
  transition: all 0.3s ease !important;
}

.send-otp-btn:hover {
  box-shadow: 0 6px 20px rgba(244, 101, 35, 0.4) !important;
  transform: translateY(-2px);
}

.info-box {
  display: flex;
  align-items: center;
  background: #e8f5e9;
  padding: 12px 16px;
  border-radius: 10px;
  font-size: 13px;
  color: #2e7d32;
  border-left: 3px solid #4caf50;
}

/* OTP Section */
.otp-section {
  animation: fadeInUp 0.4s ease;
}

.otp-header-info {
  text-align: center;
  padding: 12px 0;
  background: white;
  border-radius: 12px;
  margin-bottom: 8px;
}

.otp-sent-msg {
  font-size: 14px;
  color: #666;
  margin: 0 0 8px 0;
  font-weight: 500;
}

.mobile-display {
  font-size: 16px;
  color: #333;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.mobile-display strong {
  color: #f46523;
  font-size: 18px;
}

.otp-label {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  text-align: center;
}

.otp-inputs {
  display: flex;
  gap: 12px;
  justify-content: center;
}

.otp-input {
  max-width: 64px;
  flex: 1;
}

.otp-input >>> .v-input__control {
  background: white;
  border-radius: 12px;
}

.otp-input >>> input {
  text-align: center;
  font-size: 28px;
  font-weight: 700;
  color: #f46523;
  letter-spacing: 0;
}

.otp-input >>> .v-input__slot {
  min-height: 64px !important;
}

.otp-input >>> fieldset {
  border-width: 2px;
  border-color: #e0e0e0;
  transition: all 0.3s ease;
}

.otp-input >>> .v-input--is-focused fieldset {
  border-color: #f46523 !important;
  border-width: 2px !important;
  box-shadow: 0 0 0 3px rgba(244, 101, 35, 0.1);
}

.otp-error-message {
  text-align: center;
  font-size: 13px;
  line-height: 1.5;
  color: #d32f2f;
  background: #fff4f4;
  border: 1px solid #ffd5d5;
  border-radius: 10px;
  padding: 10px 12px;
}

.verify-btn {
  border-radius: 12px !important;
  font-weight: 600 !important;
  letter-spacing: 0.5px;
  text-transform: none !important;
  font-size: 16px !important;
  box-shadow: 0 4px 12px rgba(244, 101, 35, 0.3) !important;
  transition: all 0.3s ease !important;
}

.verify-btn:hover {
  box-shadow: 0 6px 20px rgba(244, 101, 35, 0.4) !important;
  transform: translateY(-2px);
}

.resend-section {
  text-align: center;
  padding: 16px;
  background: white;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.resend-text {
  font-size: 13px;
  color: #666;
  font-weight: 500;
}

.resend-btn {
  text-transform: none !important;
  font-weight: 600 !important;
}

/* Animations */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Responsive */
@media (max-width: 960px) {
  .service-details-card {
    padding: 16px;
  }

  .service-title {
    font-size: 22px;
  }

  .price-amount {
    font-size: 24px;
  }

  .scrollable-content {
    max-height: 350px;
  }
}

@media (max-width: 600px) {
  .service-details-card {
    padding: 16px;
  }

  .service-title {
    font-size: 20px;
  }

  .price-amount {
    font-size: 22px;
  }

  .section-heading {
    font-size: 15px;
  }

  .scrollable-content {
    max-height: 400px;
  }
}

.location-how-to-box {
  text-align: left;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 10px;
  padding: 16px 20px;
  margin: 24px auto 0;
  max-width: 520px;
}
.location-how-to-title {
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 12px;
  display: flex;
  align-items: center;
}
.location-how-to-steps { font-size: 13px; color: #4b5563; line-height: 1.6; }
.location-how-to-steps ol { margin: 0 0 0 18px; padding: 0; }
.location-how-to-steps li { margin-bottom: 6px; }
.location-how-to-browser { font-size: 13px; color: #374151; }
.location-guide-links { font-size: 13px; padding-top: 8px; border-top: 1px solid #e5e7eb; }
.location-guide-links-label { color: #6b7280; margin-right: 8px; }
.location-guide-link { color: #f46523; font-weight: 600; text-decoration: none; }
.location-guide-link:hover { text-decoration: underline; }
.location-guide-sep { color: #9ca3af; margin: 0 6px; }
.location-tip { font-size: 13px; color: #6b7280; line-height: 1.5; max-width: 520px; margin-left: auto; margin-right: auto; }
</style>
